import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { OnlineStateService } from 'services/online-state.service';
import { PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { UserDb } from 'services/user-db.service';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { LoadingController } from 'providers/loading-controller';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { CoachClientSlot, CoachClientStatus, CoachIntakeMode, ClientIntakeStatus, CoachRelationship, coachRelAsClientId, isSlotSleeping } from '@cf-shared/data-model/coach';
import { BadgeColor } from 'components/cs-badge/cs-badge';
import { CoachClientDetailPageParams } from './coach-client-detail-interface';
import { IIntakeFormPage } from 'pages/intake-form/intake-form-interface';
import { promptCoachNicknameAsync } from 'pages/coach-settings/coach-settings';
import { isClientProgramReady } from 'pages/coach-settings/training-overview-entry';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { AuthService } from 'services/auth.service';
import { CoachClientEntryService } from 'services/coach-client-entry.service';
import { ConfigDbService } from 'services/config-db.service';
import { isCoachingDashboardOnAsync } from 'services/coaching-dashboard-flag';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { trySystemShare } from 'services/system-share';
import { LL } from 'i18n/i18n.service';
import { CoachClientDataService } from 'services/coach-client-data.service';
import { CoachDemoSeedService } from 'services/coach-demo-seed.service';
import {
  CoachActionItem,
  DEFAULT_ACTION_ITEM_WINDOW_DAYS,
  HandoverState,
  checkClientHandover,
  coachClientLogReadRangeUtc,
  detectActionItems,
  deepLinkPageForItem,
} from 'services/coach-dashboard-helpers';
import { formatTimeSince } from 'services/coach-presence-helpers';
import { isLogRecordEntry, Log_Entry } from '@cf-shared/data-model/user-log';
import { LoggingService } from 'services/logging.service';
import { TranslatedString } from 'models/translated-string';
import { DateTime } from 'luxon';
import { describeAsleepDuration } from 'util/coach-sleep-display';
import { describeErrorAndReportIfUnexpected } from 'util/version-mismatch-error-policy';
import { CLAIM_LINK_UPLOAD_WAIT_MS, fetchClaimLinkWithRetryAsync } from './claim-link-retry';
import { describeError } from '@cf-shared/error-describe';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { isDatabaseTornDown, isDatabaseUnavailable } from 'services/database-unavailable-error';

/**
 * Detail view for a single coach client slot. Displayed as a content-fit modal.
 *
 * Three UI states based on slot status and intake data:
 * - Pending, no intake: empty state + "Fill Intake" button
 * - Pending, has intake: plan summary + inline invite link + "Share" button
 * - Claimed: client display name + Active badge + disabled "Manage" button
 *
 * The client nickname is displayed as a clickable header title that opens an
 * ellipsis menu with Rename, Edit Program (when applicable), and Remove Client.
 *
 * Removal flow:
 * - Pending slots: deleted locally via PouchDB (no server call needed)
 * - Claimed slots: coachDetachClient server function handles cross-database
 *   cleanup (relationships + slot deletion). The UI is hidden immediately by
 *   nulling this.slot to prevent stale state flashing during the async call.
 */
@Component({
  selector: 'page-coach-client-detail',
  templateUrl: 'coach-client-detail.html',
  host: { 'class': 'coach-client-detail' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 600, height: 'auto' }) }],
})
export class CoachClientDetailPage extends CsPage<CoachClientDetailPageParams> {
  protected LL = LL;
  slot: CoachClientSlot;
  CoachClientStatus = CoachClientStatus;
  claimUrl = '';
  loadingLink = false;
  // counts fetchClaimLink calls, so an older fetch still retrying after the coach left and came
  // back can tell it has been replaced, and leaves the screen to the newer one (#6113)
  private claimLinkFetchGeneration = 0;
  /** #4447: why the invitation link could not be created, shown in place of the link. Empty string
   *  means "no failure" — the link either loaded or is still loading. */
  claimLinkError: TranslatedString | '' = '';
  /** Pending action items for a claimed client (weekly checkups awaiting
   *  review, strength tests awaiting review). Populated on enter for
   *  claimed slots from the same 30-day log window the dashboard uses;
   *  undefined while the fetch is in flight, [] when none found. */
  actionItems?: CoachActionItem[];
  /** Whether this client's account shows the coach's handover finishing (#4817).
   *  Starts NotChecked so nothing is claimed before the read comes back. */
  handover = HandoverState.NotChecked;
  loadingActionItems = false;
  /** the coaching dashboard switch (#5456), for the signed-in account, which on this page is the coach */
  private coachingDashboardOn = false;
  private docWatcher: DocWatcher;

  constructor(
    public navCtrl: NavController,
    private userDb: UserDb,
    private alertCtrl: AlertController,
    private popupCtrl: PopupMenuProvider,
    private cloudFunctions: CloudFunctionsService,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private auth: AuthService,
    private coachClientData: CoachClientDataService,
    private demoSeed: CoachDemoSeedService,
    private cd: ChangeDetectorRef,
    private onlineState: OnlineStateService,
    private logging: LoggingService,
    private configDb: ConfigDbService,
    private clientEntry: CoachClientEntryService,
  ) {
    super();
  }

  async ionViewWillEnter() {
    this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
      if (this.viewDestroyed) return;
      this.cd.markForCheck();
    });
    try {
      this.slot = await this.userDb.database.getRecordAsync(this.navParams.data.clientSlotId, this.docWatcher) as CoachClientSlot;
    } catch (err) {
      // Deliberately NOT awaited: this hook runs while the page is animating in, and
      // `cs-nav` waits on the promise it returns before calling `ionViewDidEnter`. The
      // failure handling below waits for the coach to tap OK, which is however long
      // they take — awaiting it here would hold that hook open and be reported as a
      // page that hung on entry.
      void this.handleSlotLoadFailureAsync(err);
      return;
    }
    if (this.viewDestroyed) return;
    this.cd.markForCheck();

    if ((this.hasIntake && this.isPending) || this.isClientFillsPending) {
      // option A: intake form is filled, fetch the claim link to display
      // option B (#3389): client-fills slot already has intakeMode set; the
      // claim link can be minted with no intake (PRD Step 4 branch on the server)
      void this.fetchClaimLink();
    }

    // #4530: skipped for a parked client. The page does not show their action
    // items — the coach is refused entry, so every row would be something they
    // cannot act on — and fetching them anyway would have the coach's app read
    // a parked client's own log history, which is what parking is meant to stop.
    if (this.isClaimed && this.slot.clientUid && !this.isAsleep) {
      // #5456: read the switch before the rows are drawn, so a row is a button or plain text from its first render
      this.coachingDashboardOn = await isCoachingDashboardOnAsync(this.configDb);
      if (this.viewDestroyed) return;
      void this.loadActionItemsAsync(this.slot.clientUid);
      // #4817: same conditions, for the same reasons — a parked client's account
      // is not read, and only a claimed client has a handover to judge.
      void this.loadHandoverStateAsync(this.slot.clientUid);
    }
  }

  /**
   * The client slot could not be read when this page opened.
   *
   * The read fails in ways that mean different things, and the page must not present them
   * as one: `DatabaseUnavailableError` means "I could not look", NOT "there is nothing
   * there", so treating it as a missing slot tells a coach whose database was momentarily
   * unreadable that their client does not exist.
   *
   * Our own teardown says nothing at all. Every other cause tells the coach and then takes
   * them back to the client list, which reloads from the database and so shows the truth
   * either way — the landing `removeClient` below picks, for the reason it gives there.
   *
   * Three separate report NAMES rather than one name plus a flag in the context: Sentry
   * indexes the name and treats the context as a breadcrumb, so a cause named only there
   * would collapse all three into one issue.
   */
  private async handleSlotLoadFailureAsync(err: unknown): Promise<void> {
    // if the wrapper refused because it is being torn down, this is our own doing and not
    // a fault. Expected: a `DatabaseUnavailableError` carrying a teardown reason, raised by
    // the `getRecordAsync` above when a sign-out wipe (chiefly) shuts the wrapper down
    // while this page is opening. Saying nothing is the point — the app is on its way out,
    // so an alert would land over the sign-out and a report would be us reporting
    // ourselves. `isDatabaseTornDown` is the test rather than
    // `DatabaseRecoveryService.isIntentionalDestruction()`; the helper explains why that flag is
    // still false in this window.
    if (isDatabaseTornDown(err)) {
      console.log('coach-client-detail: client slot read refused, the database is shutting down:', err);
      return;
    }

    // Report BEFORE the destroyed check below. The coach can close this modal while the
    // read is still in flight, and if the check came first the failure would then reach
    // nobody at all. Only the alert depends on the page still being there.
    const slotIsGone = isPouchNotFound(err);
    const reportContext = {
      clientSlotId: this.navParams.data.clientSlotId,
      error: describeError(err),
    };
    if (slotIsGone) {
      this.logging.trackSilentInfo('coach-client-detail: client slot no longer exists', reportContext);
    } else if (isDatabaseUnavailable(err)) {
      // what is left of the unavailable family here is the transient half — a recovery cut
      // the read short, or start-up has not reached that document yet — so the same read a
      // moment later may well work.
      this.logging.trackSilentProblem('coach-client-detail: client slot could not be read (database temporarily unavailable)', reportContext);
    } else {
      this.logging.trackSilentProblem('coach-client-detail: client slot could not be read', reportContext);
    }

    if (this.viewDestroyed) return;

    // Message-only alert, like the removal-failure one below: this page's own title is
    // the client's nickname, which we do not have.
    await this.alertCtrl.showAlertAsync({
      message: slotIsGone ? LL.coach.clientGoneMessage() : LL.common.errors.generic(),
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });

    if (this.viewDestroyed) return;
    await this.navCtrl.popAsync();
  }

  /**
   * Fetch the same 30-day action-item window the dashboard uses, then
   * run `detectActionItems` so the detail page can list each pending
   * weekly checkup / strength test the client owes the coach.
   *
   * Demo-seed short-circuit: synthetic uids (TestUI "Become a demo
   * coach") return their canned row's items without a network call —
   * mirrors the same short-circuit in `CoachSettingsPage.computeRowAsync`.
   */
  private async loadActionItemsAsync(clientUid: string): Promise<void> {
    // demo: a "stuck loading" synthetic uid keeps the skeleton on
    // indefinitely on this page too, mirroring the same short-circuit
    // in `CoachSettingsPage.computeRowAsync` so the demo presents a
    // consistent loading row whether viewed in the dashboard or in
    // detail. We set loadingActionItems = true and return BEFORE the
    // try block so the `finally` does not flip it back to false.
    if (this.demoSeed.isSyntheticLoading(clientUid)) {
      this.loadingActionItems = true;
      this.cd.markForCheck();
      return;
    }
    this.loadingActionItems = true;
    this.cd.markForCheck();
    try {
      if (this.demoSeed.isSynthetic(clientUid)) {
        this.actionItems = this.demoSeed.getSyntheticRow(clientUid)?.items ?? [];
        return;
      }
      const today = this.userDb.debug.getNowLocal();
      const { startUtc, endUtc } = coachClientLogReadRangeUtc(today);
      const docs = await this.coachClientData.readClientLogRangeAsync<Log_Entry>(clientUid, startUtc, endUtc);
      // drop non-entry journal snapshots (FitnessPlan / IntakeForm) that share the
      // `log/` id prefix but are not log entries (no `type`). detectActionItems already
      // skips docs without time_utc, so this is correctness-by-construction for
      // consistency with the coach-dashboard reader (fetchClientLogsAsync), not a
      // behavior change here.
      const logs = docs.filter(isLogRecordEntry);
      if (this.viewDestroyed) return;
      this.actionItems = detectActionItems(logs, today.toUTC(), DEFAULT_ACTION_ITEM_WINDOW_DAYS);
    } finally {
      this.loadingActionItems = false;
      this.cd.markForCheck();
    }
  }

  /**
   * Ask whether this client's account shows the coach's handover finishing
   * (#4817), so the page can explain the warning the client list shows.
   *
   * One extra document read, on a page the coach opened deliberately about one
   * client. The dashboard gets the same answer for free because it already
   * fetches a batch of the client's singletons; here there is no such batch to
   * ride along with, and the alternative — showing a warning badge in the list
   * with no explanation anywhere — is worse than one small request.
   *
   * The read reports whether it got an ANSWER separately from what it found
   * (`ClientDocsRead.ok`), and that distinction is the whole point here: an
   * absent coach link IS the failure this feature reports, so it must never be
   * mistaken for "we could not look".
   */
  private async loadHandoverStateAsync(clientUid: string): Promise<void> {
    if (this.demoSeed.isSynthetic(clientUid)) return;
    const read = await this.coachClientData.readClientDocsAsync(clientUid, [coachRelAsClientId]);
    // `this.slot` as well as the view: removeClient clears the slot to hide the UI
    // while the removal is in flight, and does NOT destroy the page — so a read
    // still outstanding at that moment would otherwise resume against no slot.
    if (this.viewDestroyed || !this.slot) return;
    this.handover = checkClientHandover({
      coachUid: this.auth.user?.uid || '',
      slot: this.slot,
      clientCoachRel: read.docs.get(coachRelAsClientId) as CoachRelationship | undefined,
      couldReadClientAccount: read.ok,
      now: this.userDb.debug.getNowLocal().toUTC(),
    });
    this.cd.markForCheck();
  }

  /** Whether to explain, on this page, that the client's account never received
   *  the handover. Parked clients are excluded for the same reason their
   *  on-track status is hidden — see `checkClientHandover`. */
  get hasBrokenHandover(): boolean {
    return !this.isAsleep && this.handover === HandoverState.Missing;
  }

  /** Title for an action-item row, e.g. "Weekly checkup" or "Strength test". */
  getActionItemTitle(item: CoachActionItem): string {
    return item.type === 'checkup'
      ? LL.coach.dashboard.actionItemCheckupTitle()
      : LL.coach.dashboard.actionItemStrengthTestTitle();
  }

  /** Relative-time label ("3d ago") reusing the presence formatter so
   *  date wording stays consistent across the coach UI. */
  getActionItemTimeAgo(item: CoachActionItem): string {
    return LL.coach.dashboard.actionItemTimeAgo({ time: formatTimeSince(item.dateUtc) });
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  get hasIntake(): boolean {
    return !!this.slot?.intakeFormState;
  }

  get isClaimed(): boolean {
    return this.slot?.status === CoachClientStatus.Claimed;
  }

  get isPending(): boolean {
    return this.slot?.status === CoachClientStatus.Pending;
  }

  // option B (#3389): the slot is a client-fills slot (the coach chose
  // "Send blank intake to client"). Absent intakeMode defaults to CoachFills.
  get isClientFills(): boolean {
    return this.slot?.intakeMode === CoachIntakeMode.ClientFills;
  }

  // option B (#3389): the coach has chosen the client-fills path but no
  // client has claimed the link yet. The claim link can still be displayed
  // so the coach can share it.
  get isClientFillsPending(): boolean {
    return this.isPending && this.isClientFills;
  }

  // option B (#3389): client-fills lifecycle markers used to drive the
  // status-driven action UI when the slot has been claimed.
  get isClientFillsInvited(): boolean {
    return this.isClaimed && this.isClientFills && this.slot.clientIntakeStatus === ClientIntakeStatus.Invited;
  }
  get isClientFillsSubmitted(): boolean {
    return this.isClaimed && this.isClientFills && this.slot.clientIntakeStatus === ClientIntakeStatus.Submitted;
  }
  /** option A claimed, or option B finalized: the normal active client view with Manage Client.
   *  The same rule decides the Training Overview button on the Coaching list (#6795). */
  get isProgramReady(): boolean {
    return isClientProgramReady(this.slot);
  }

  // option A (existing flow): the coach fills the intake themselves on
  // their own account. Sets intakeMode = CoachFills explicitly so the slot
  // record reflects the chosen path; absent intakeMode means the same
  // thing, but writing it makes future code (and the design doc) easier.
  async chooseCoachFillsIntake() {
    this.slot.intakeMode = CoachIntakeMode.CoachFills;
    await this.userDb.database.saveRecordAsync(this.slot, DatabaseWrapper.getTransactionId());
    if (this.viewDestroyed) return;
    this.fillIntake();
  }

  // option B (#3389): the coach sends a blank intake to the client. The
  // server (coachCreateClaimToken, PRD Step 4) accepts a slot with no
  // intakeFormState as long as intakeMode is ClientFills, so we just save
  // the mode and let the claim link fetch fire.
  async chooseSendBlankIntake() {
    this.slot.intakeMode = CoachIntakeMode.ClientFills;
    await this.userDb.database.saveRecordAsync(this.slot, DatabaseWrapper.getTransactionId());
    if (this.viewDestroyed) return;
    this.cd.markForCheck();
    void this.fetchClaimLink();
  }

  fillIntake() {
    void this.openIntakeFormIfAwakeAsync('new');
  }

  editProgram() {
    void this.openIntakeFormIfAwakeAsync('change');
  }

  /**
   * Open the intake form for this client, unless the coach has parked them
   * (#4530).
   *
   * Editing a parked client's program is refused for the same reason opening
   * their account is: while they are asleep the coach is not coaching them, and
   * a change made now would reach a client who has been told nothing about it.
   *
   * `fillIntake` is guarded too, even though it is only offered before a client
   * has accepted their invite and a pending slot can never be parked. Guarding
   * it costs one line and means nobody has to re-derive that argument if the
   * page's flow changes.
   */
  private async openIntakeFormIfAwakeAsync(mode: 'new' | 'change'): Promise<void> {
    if (!await this.canActOnClientAsync()) {
      return;
    }
    void this.navCtrl.pushAsync(IIntakeFormPage, {
      mode,
      coachClientSlotId: this.slot._id,
    });
  }

  /** Whether the coach may act on this client (#4530); shows the refusal when not. See `CoachClientEntryService`. */
  private canActOnClientAsync(): Promise<boolean> {
    return this.clientEntry.canActOnClientAsync(this.slot);
  }

  /** Badge colour for a parked client — the neutral one, matching the client
   *  list: parking is an ordinary choice the coach made, not a problem. */
  readonly asleepBadgeColor = BadgeColor.Gray;

  /** Whether the coach has parked this client (#4530). */
  get isAsleep(): boolean {
    return isSlotSleeping(this.slot);
  }

  /** "Asleep for 12 days", or "Paused for 12 days because your plan is full"
   *  when the server did the parking (#4454). Only read while asleep. */
  asleepForText(): TranslatedString {
    return describeAsleepDuration(this.slot, DateTime.now());
  }

  showMoreMenu(ev: Event) {
    const buttons: any[] = [
      {
        text: LL.coach.rename(),
        icon: 'pencil',
        handler: () => { void this.renameClient(); },
      },
    ];

    // Parking a client is offered only for a claimed one: a pending invite has
    // nobody behind it yet, so there is nobody to park. Renaming stays
    // available either way — it is the coach's own bookkeeping and has nothing
    // to do with the client's access.
    if (this.isClaimed) {
      buttons.push(this.isAsleep
        ? {
          text: LL.coach.wakeClient.button(),
          icon: 'sunny',
          handler: () => { void this.setClientSleeping(false); },
        }
        : {
          text: LL.coach.sleepClient.button(),
          icon: 'moon',
          handler: () => { void this.setClientSleeping(true); },
        });
    }

    buttons.push({
      text: LL.coach.removeClient.button(),
      icon: 'close',
      cssClass: 'danger',
      handler: () => { void this.removeClient(); },
    });

    void this.popupCtrl.createMenu({
      targetFromElement: ev.target as HTMLElement,
      buttons,
    });
  }

  async renameClient() {
    const allSlots = await this.userDb.database.getByIdHeadAsync('coach-client/', this.docWatcher) as CoachClientSlot[];
    const nickname = await promptCoachNicknameAsync(
      this.alertCtrl, allSlots,
      { mode: 'rename', currentValue: this.slot.nickname },
    );
    if (!nickname || nickname === this.slot.nickname) return;

    this.slot.nickname = nickname;
    // This save writes the WHOLE slot document, including the sleeping fields
    // (#4530) that only the server sets, and that is safe — see
    // `design-docs/coach-mode.md` → "Sleeping clients" for why, and for the
    // rule that actually carries the weight (waking must write a value, never
    // delete one).
    await this.userDb.database.saveRecordAsync(this.slot, DatabaseWrapper.getTransactionId());
    this.cd.markForCheck();
  }

  /**
   * Park this client, or wake them again (#4530).
   *
   * Parking is the reversible alternative to removing: the client keeps their
   * account, program and history, but goes back to the free experience, cannot
   * be opened by the coach, and stops taking up a place on the coach's plan.
   *
   * Follows `removeClient` below for its spinner and error-handling shape, with
   * one deliberate difference: it does NOT clear `this.slot` first. Removing
   * does that to avoid showing a client who is about to disappear; parking
   * leaves the client right where they are, so blanking the page would be a lie
   * about what just happened.
   */
  async setClientSleeping(sleeping: boolean) {
    const texts = sleeping ? LL.coach.sleepClient : LL.coach.wakeClient;
    if (!await this.alertCtrl.showAlertAsync({
      title: texts.confirmTitle(),
      message: texts.confirmMessage(),
      buttons: [
        { text: texts.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: texts.confirm(), role: AlertButtonRole.Primary, result: true },
      ],
    })) {
      return;
    }

    const slotId = this.slot._id;
    const loading = this.loadingCtrl.create({ content: texts.working() });
    void loading.present();

    // holds whatever the call threw, so the message is shown only after the
    // loading overlay has been dismissed. Stays undefined on success.
    let failure: unknown = undefined;
    try {
      // The server writes the slot in the coach's database, so wait for that
      // write to sync back before dropping the spinner. Without the wait the
      // coach would be handed back a page still drawing the OLD state, and the
      // obvious reaction — press it again — is a no-op that looks like the
      // feature is broken.
      const syncPromise = this.userDb.database.waitForDocChangeAsync(slotId);
      await this.cloudFunctions.coachSetClientSleepingAsync({ clientSlotId: slotId, sleeping });
      await syncPromise;
    } catch (err) {
      failure = err;
    } finally {
      void loading.dismiss();
    }

    if (failure !== undefined) {
      // Describe and REPORT the failure first, before the destroyed check
      // below. That ordering is the point: the coach can walk away from this
      // page while the call is still in flight, and if the check came first a
      // failure would then reach nobody — no alert for the coach and no report
      // for us either. The client's access would silently not have changed.
      // Shared policy (wording plus what reaches Sentry): see the helper.
      const failureMessage = describeErrorAndReportIfUnexpected(failure, this.logging, LL.common.errors.generic());
      // Only the ALERT depends on the page still being there. An alert raised
      // on a page the coach has left would appear over whatever they moved on
      // to, with no context.
      if (this.viewDestroyed) return;
      // Message-only alert, like the removal-failure one below: the confirm
      // dialog's own title would read as if the change had happened.
      await this.alertCtrl.showAlertAsync({
        message: failureMessage,
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
    }

    if (this.viewDestroyed) return;
    // the app is zoneless, so a correctly-updated model still draws the old
    // page without this
    this.cd.markForCheck();
  }

  async removeClient() {
    if (!await this.alertCtrl.showAlertAsync({
      title: LL.coach.removeClient.confirmTitle(),
      message: this.isClaimed
        ? LL.coach.removeClient.confirmMessageClaimed()
        : LL.coach.removeClient.confirmMessage(),
      buttons: [
        { text: LL.coach.removeClient.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.coach.removeClient.confirm(), role: AlertButtonRole.Primary, result: true },
      ],
    })) {
      return;
    }

    const slotToRemove = this.slot;
    // hide content immediately to prevent stale UI flash during async removal
    this.slot = undefined;
    this.cd.markForCheck();

    const loading = this.loadingCtrl.create({ content: LL.coach.removeClient.removing() });
    void loading.present();

    // holds whatever the removal threw, so the message is shown only after the
    // loading overlay has been dismissed. stays undefined when removal succeeds.
    let removalError: unknown = undefined;
    try {
      if (slotToRemove.status === CoachClientStatus.Claimed) {
        // server detaches relationships and deletes the slot in CouchDB;
        // wait for the deletion to sync back to local PouchDB before navigating,
        // so the parent page shows the updated client list
        const syncPromise = this.userDb.database.waitForDocChangeAsync(slotToRemove._id);
        await this.cloudFunctions.coachDetachClientAsync({ clientSlotId: slotToRemove._id });
        await syncPromise;
      } else {
        await this.userDb.database.deleteRecordAsync(slotToRemove, DatabaseWrapper.getTransactionId());
      }
    } catch (err) {
      // the removal can THROW — coachDetachClient reads the coach's `_users` doc on
      // the server, and since #4283 that read REFUSES (HTTP 409) when the doc is
      // newer than the live server understands. `this.slot` was already cleared
      // above to avoid a stale-UI flash, so a throw that escaped this method left
      // the user on a blank page: no client shown, no message, and no pop back to
      // the client list.
      // only remember the failure here; the message is shown after the `finally`
      // below, so the loading overlay is already gone when the alert appears.
      removalError = err;
    } finally {
      void loading.dismiss();
    }

    // if the removal failed, say so before leaving the page
    if (removalError !== undefined) {
      // shared policy (wording plus what reaches Sentry): see the helper.
      // message-only alert, like the impersonation-failure one further down: the
      // confirm dialog's own title would read as if the removal had happened.
      await this.alertCtrl.showAlertAsync({
        message: describeErrorAndReportIfUnexpected(removalError, this.logging, LL.common.errors.generic()),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
    }
    // go back to the client list either way: it reloads from the database, so it
    // shows the truth whether the client was removed or the removal failed.
    await this.navCtrl.popAsync();
  }

  /**
   * Ask the server for this client's invitation link.
   *
   * #4447: when both attempts fail, this used to end in a bare `console.error`. That reaches nobody
   * on a customer's device, and the template renders nothing at all when `claimUrl` is empty — so
   * the coach was left looking at the "share this link with your client" description with no link,
   * no message and no Share button under it, which is indistinguishable from "this client has no
   * link yet". Now the failure is both reported and shown, with a way to try again.
   */
  private async fetchClaimLink() {
    const generation = ++this.claimLinkFetchGeneration;
    const isStale = () => this.viewDestroyed || generation !== this.claimLinkFetchGeneration;
    this.loadingLink = true;
    // clear any message from a previous attempt, so a successful retry does not leave the old
    // failure sitting on screen underneath the new link
    this.claimLinkError = '';
    this.cd.markForCheck();

    // the server reads CouchDB, which can lag behind local PouchDB right after the intake form is
    // filled in or "Send Blank Intake" is picked. So wait for the upload first (#6792), then retry
    // while the server may still be catching up. The schedule: claim-link-retry.ts (#6113)
    try {
      const result = await fetchClaimLinkWithRetryAsync(
        () => this.cloudFunctions.coachCreateClaimTokenAsync({ clientSlotId: this.slot._id, coachName: this.auth.user.name }),
        {
          sleepAsync: ms => new Promise<void>(r => this.safeTimeout(() => r(), ms)),
          isCancelled: isStale,
          waitForLocalWritesUploadedAsync: () => this.waitForSlotUploadAsync(),
        });
      if (result === undefined || isStale()) return;
      this.claimUrl = `${getSharedUrlOrigin()}/login?coachInviteToken=${encodeURIComponent(result.claimToken)}`;
    } catch (err) {
      console.error('fetchClaimLink failed after retries:', err);
      // shared policy (the wording plus what reaches Sentry): see the helper. It keeps the
      // #4283 "this server is behind that account's data" refusal's own honest wording and
      // keeps it out of Sentry, because that condition fixes itself on the next deploy; every
      // other cause — an expired session, a server error, a coach at their client-slot limit —
      // is reported with its full stack and shown under the generic wording.
      const message = describeErrorAndReportIfUnexpected(err, this.logging, LL.coach.invite.linkFailed());
      // if a newer fetch has taken over, it owns the screen; this one has still reported its failure
      if (isStale()) return;
      this.claimLinkError = message;
    }
    this.loadingLink = false;
    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  // the wait only makes the first call likelier to succeed, so if the check itself cannot run
  // (it reads sessionStorage, which a hardened browser can refuse), report that and ask anyway
  private async waitForSlotUploadAsync(): Promise<void> {
    // offline nothing can upload, so waiting would only hold back the failure message by 15s;
    // a wrong "offline" answer just skips the wait, which is how the page behaved before #6792
    if (!this.onlineState.isLikelyOnline()) return;
    try {
      await this.userDb.database.whenPushDrainedAsync(CLAIM_LINK_UPLOAD_WAIT_MS);
    } catch (err) {
      this.logging.trackSilentProblem('ClaimLinkUploadWaitFailed', { error: describeError(err) });
    }
  }

  /** Coach tapped "Try Again" under a failed invitation link. */
  retryClaimLink() {
    // a fast double-tap would otherwise start two overlapping fetches. That is not merely wasteful:
    // when the slot has no token yet, both calls mint one and save the slot, so one can lose on a
    // document conflict and surface as a failure the coach never caused.
    if (this.loadingLink) return;
    void this.fetchClaimLink();
  }

  async copyLink() {
    await navigator.clipboard.writeText(this.claimUrl);
    this.toastCtrl.showToast({
      message: LL.coach.invite.linkCopied(),
      type: ToastType.SuccessLight,
      duration: 2000,
    });
  }

  // present the system share sheet for the claim link, fall back to clipboard + alert if unavailable
  async shareLink() {
    const result = await trySystemShare({
      title: LL.coach.invite.title(),
      url: this.claimUrl,
    });
    if (result !== 'unavailable') return;
    // if share not supported, fall back to clipboard + alert
    await navigator.clipboard.writeText(this.claimUrl);
    await this.alertCtrl.showAlertAsync({
      title: LL.coach.invite.linkCopied(),
      message: LL.coach.invite.linkCopiedMessage(),
      buttons: [
        { text: LL.coach.invite.ok(), role: AlertButtonRole.Primary, result: true },
      ],
    });
  }

  manageClient(event: Event) {
    lockClick(event, this.clientEntry.enterClientAccountAsync(this.slot));
  }

  // option B (#3389): client has submitted their intake; the coach taps
  // Review Intake to enter the client's account and auto-open the intake
  // wizard pre-filled with the client's answers ('intake-review' is the intake
  // form in change mode, opened on the post-reload boot).
  reviewIntake(event: Event) {
    lockClick(event, this.clientEntry.enterClientAccountAsync(this.slot, 'intake-review'));
  }

  // option B (#3389): stall fallback — client was invited but has not
  // finished. The coach decides to fill the intake themselves inside the
  // client's account. Same entry as reviewIntake — the intake form runs
  // in change mode (revealed tabs include BasicInfo and Strength) so the
  // coach can fill any empty answer.
  fillIntakeMyself(event: Event) {
    lockClick(event, this.clientEntry.enterClientAccountAsync(this.slot, 'intake-review'));
  }

  /**
   * Whether a pending action item opens the thing to review when tapped (#5456, phase 2). Only
   * with the coaching dashboard switch on, and never for a demo client: its rows are made up and
   * point at no real log.
   */
  get areActionItemsClickable(): boolean {
    return this.coachingDashboardOn && !!this.slot?.clientUid && !this.demoSeed.isSynthetic(this.slot.clientUid);
  }

  /**
   * Open the thing an action item asks the coach to review: enter the client's account at that
   * check-up's review screen, or at that workout in the coach's view-only mode.
   */
  onActionItemClick(event: Event, item: CoachActionItem) {
    if (!this.areActionItemsClickable) return;
    lockClick(event, this.clientEntry.enterClientAccountAsync(this.slot, deepLinkPageForItem(item)));
  }

  getPlanSummary(): string {
    if (!this.slot?.fitnessPlan) return '';
    const plan = this.slot.fitnessPlan;
    const parts: string[] = [];
    if (plan.nominalDailyAverageIntake) {
      parts.push(`${Math.round(plan.nominalDailyAverageIntake)} kcal/day`);
    }
    if (plan.trainingVolumeWeeklyAvg) {
      parts.push(`${Math.round(plan.trainingVolumeWeeklyAvg)} sets/week`);
    }
    if (this.slot.intakeFormState?.trainingDays) {
      const days = this.slot.intakeFormState.trainingDays.filter(d => d).length;
      parts.push(`${days} training days`);
    }
    return parts.join(' \u00B7 ');
  }
}
